import { useCallback, useMemo, useRef } from 'react';

import type { UUID } from '@aline/common';
import type {
  ActivityComposerHandle,
  ActivityFeedHandle,
  ActivityItemFragment,
  ComposerLane,
} from 'src/components';
import { useActivityNavigation } from 'src/components/activity/use-activity-navigation';
import { useScrollFeedOnSubmit } from 'src/components/activity/use-scroll-feed-on-submit';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import type { useGetTaskWithComments } from 'src/hooks';

import { getAssignmentUser, getLaneMentionFilter } from './task-review-helpers';
import type { TaskReviewAssignmentsHandle } from './TaskReviewAssignments';
import { useActivityLane } from './use-activity-lane';
import { useTaskActivityController } from '../TaskActivitySection';
import { useThreadDeepLinkHandlers } from '../use-thread-deep-link-handlers';

export type TaskWithComments = NonNullable<ReturnType<typeof useGetTaskWithComments>['task']>;

export type TaskPanelState = ReturnType<typeof useTaskPanelState>;

/**
 * Everything a task view wires up besides its fields: the activity feed and its lane, the
 * composer and its draft, and the refs that let the fields scroll the feed or reveal reviewers.
 * The workflow side panel and the task page lay these out differently but share the wiring.
 */
export function useTaskPanelState({
  isAiBuilding,
  isPreview,
  task,
  workflowId,
}: {
  isAiBuilding: boolean;
  isPreview: boolean;
  task: TaskWithComments;
  workflowId: UUID;
}) {
  const isTemplateTask = task.variant === Apollo.TaskVariant.Template;
  const navigation = useActivityNavigation(task.id);
  const activityLane = useActivityLane({
    anchorId: navigation.request?.activityItemId ?? null,
    isPreview,
    skip: isTemplateTask,
    task,
  });
  const activityController = useTaskActivityController(task.id, workflowId, {
    audience: activityLane.audience,
    navigation,
    skip: isTemplateTask || activityLane.resolving,
  });
  const { onViewThread } = useThreadDeepLinkHandlers({ taskId: task.id, workflowId });
  const feedRef = useRef<ActivityFeedHandle | null>(null);
  const { handleSubmitAI, handleSubmitComment } = useScrollFeedOnSubmit(feedRef, {
    onSubmitAI: activityController.onSubmitAI,
    onSubmitComment: activityController.onSubmitComment,
  });
  const handleAlineAiActivityStarted = useCallback(
    (activityItem: ActivityItemFragment) => {
      activityController.addActivityItem(activityItem);
      feedRef.current?.scrollToBottom();
    },
    [activityController]
  );
  const composerLane = useComposerLane(task, activityLane.audience);
  const composerRef = useRef<ActivityComposerHandle | null>(null);
  const isTeamOnlyLane = composerLane?.audience === Apollo.ActivityAudience.Internal;
  const composerDraft = useMemo<ActivityComposerHandle | null>(
    () =>
      isTeamOnlyLane
        ? null
        : {
            clear: () => composerRef.current?.clear(),
            getDraft: () => composerRef.current?.getDraft() ?? '',
          },
    [isTeamOnlyLane]
  );
  const reviewersRef = useRef<TaskReviewAssignmentsHandle | null>(null);

  return {
    activityController,
    activityLane,
    composerDraft,
    composerLane,
    composerRef,
    feedRef,
    handleAlineAiActivityStarted,
    handleSubmitAI,
    handleSubmitComment,
    isExternalView: !task.requesterIsInternal || isPreview,
    isReadOnly: Boolean(task.completedAt) || Boolean(task.rejectedAt) || isAiBuilding,
    isTemplateTask,
    onViewThread,
    reviewersRef,
  };
}

/** The lane new comments post into, and who the @ menu offers there. */
function useComposerLane(
  task: TaskWithComments,
  laneAudience: Apollo.ActivityAudience | null
): ComposerLane | undefined {
  const viewerOrgId = useSelectCurrentUser()?.org?.id ?? null;
  return useMemo(() => {
    // An external viewer has no switch but always writes in the External lane.
    const audience =
      laneAudience ?? (!task.requesterIsInternal ? Apollo.ActivityAudience.External : null);
    if (!audience) return undefined;

    const users = task.assignments.flatMap(assignment => {
      const user = getAssignmentUser(assignment);
      return user ? [{ id: user.id, isExternal: assignment.isExternal }] : [];
    });
    return {
      audience,
      canMentionUser: getLaneMentionFilter({
        audience,
        externalUserIds: new Set(users.filter(user => user.isExternal).map(user => user.id)),
        taskUserIds: new Set(users.map(user => user.id)),
        viewerOrgId,
      }),
      peopleOnly: audience === Apollo.ActivityAudience.External,
    };
  }, [laneAudience, task.assignments, task.requesterIsInternal, viewerOrgId]);
}
